import {
  Box,
  FormControlSizes,
  FuseAutocompleteResult,
  Icon,
  IconButton,
  IconName,
  type ISubaccount,
  MixpanelEvent,
  type RenderResultFunc,
  SearchSelect,
  type SearchSelectProps,
  SubAccountIcon,
  Tooltip,
  useCallbackRef,
  useFavoriteSubAccounts,
  useMixpanel,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useSubAccounts } from '../../providers';
import { NavigableControlArrow, NavigableControlRow } from '../MarketDataCard/components/NavigableControlRow';
import { SelectedFieldTooltipContent } from '../MarketDataCard/components/SelectedFieldTooltipContent';
import { MARKET_CARD_CONTROL_TOOLTIP_PROPS } from '../MarketDataCard/marketCardControlTooltipProps';

export interface ISubAccountSearchSelectProps extends Omit<SearchSelectProps<ISubaccount>, 'getLabel' | 'options'> {
  showNavigation?: boolean;
  options: ISubaccount[];
}

const labelGetter = (value: ISubaccount) => value.DisplayName || value.Name;
const style = { lineHeight: 1 };

// REFACTOR: These components can be refactored into a single FavoritesSearchSelect
// - StrategySelector.tsx
// - SubAccountSearchSelect.tsx
// - OrderPresetSearchSelect.tsx
// - AllocationSelector (maybe)

export function SubAccountSearchSelect({
  options: _options,
  showNavigation = false,
  ...props
}: ISubAccountSearchSelectProps) {
  const mixpanel = useMixpanel();
  const { subAccountsByName } = useSubAccounts();
  const { favoriteSubAccounts, setFavoriteSubAccounts } = useFavoriteSubAccounts();
  const favoriteSubAccountsSet = useMemo(() => new Set(favoriteSubAccounts), [favoriteSubAccounts]);
  const favoriteOptions = useMemo(
    () => compact([...favoriteSubAccountsSet.values()].map(name => subAccountsByName.get(name))),
    [favoriteSubAccountsSet, subAccountsByName]
  );

  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const options = useMemo(
    () => _options.filter(option => (showOnlyFavorites ? favoriteSubAccountsSet.has(option.Name) : true)),
    [showOnlyFavorites, _options, favoriteSubAccountsSet]
  );

  const toggleFavorite = useCallbackRef((subAccount: ISubaccount) => {
    const newFavorites = favoriteSubAccountsSet;
    if (newFavorites.has(subAccount.Name)) {
      newFavorites.delete(subAccount.Name);
    } else {
      mixpanel.track(MixpanelEvent.FavoriteSubAccount);
      newFavorites.add(subAccount.Name);
    }
    setFavoriteSubAccounts([...newFavorites.values()]);
  });

  const renderFavoriteIcon = useCallback(
    (subAccount: ISubaccount) => {
      const isFavorite = favoriteSubAccounts?.includes(subAccount.Name);
      return (
        <Icon
          icon={isFavorite ? IconName.StarSolid : IconName.Star}
          color={isFavorite ? 'yellow.lighten' : 'gray.080'}
          size="fontSizeLarge"
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            toggleFavorite(subAccount);
          }}
          p="spacing12"
          spaceBefore="-spacing12"
        />
      );
    },
    [toggleFavorite, favoriteSubAccounts]
  );

  const renderResult: RenderResultFunc<ISubaccount> = useCallbackRef((searchResults, disabled) =>
    FuseAutocompleteResult(searchResults, disabled, renderFavoriteIcon)
  );

  const navigableOptions = favoriteOptions.length ? favoriteOptions : options;
  const currentIndex = navigableOptions.findIndex(subAccount => subAccount.Name === props.selection?.Name);
  const nextIndex = (currentIndex + 1) % navigableOptions.length;
  const prevIndex = (currentIndex - 1 + navigableOptions.length) % navigableOptions.length;

  const searchPrefix = useMemo(
    () => (
      <Box ml="-spacing4">
        <IconButton
          ghost
          onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
          icon={showOnlyFavorites ? IconName.StarSolid : IconName.Star}
          color={showOnlyFavorites ? 'yellow.lighten' : 'gray.080'}
          className="toggle-favorites"
        />
      </Box>
    ),
    [showOnlyFavorites]
  );

  const searchSelect = (
    <SearchSelect
      searchPrefix={searchPrefix}
      prefix={<SubAccountIcon selection={props.selection} />}
      placeholder="No Sub Account"
      dropdownSize={FormControlSizes.Default}
      w="100%"
      options={options}
      dropdownWidth={300}
      getLabel={labelGetter}
      showClear={true}
      portalize={true}
      disabled={options.length === 0}
      initialSortByLabel={false}
      renderResult={renderResult}
      style={style}
      {...props}
    />
  );

  const mainField = showNavigation ? (
    <Tooltip
      {...MARKET_CARD_CONTROL_TOOLTIP_PROPS}
      tooltip={
        <SelectedFieldTooltipContent
          title="Selected Sub Account"
          lines={props.selection ? [labelGetter(props.selection)] : []}
          emptyMessage="No Sub Account"
        />
      }
    >
      {searchSelect}
    </Tooltip>
  ) : (
    searchSelect
  );

  return (
    <NavigableControlRow
      left={
        showNavigation && options.length > 0 ? (
          <NavigableControlArrow
            direction="previous"
            size={props.size}
            onClick={() => prevIndex !== currentIndex && props.onChange(navigableOptions.at(prevIndex))}
            className="subaccount-prev"
            tooltip="Previous Favorite Sub Account"
          />
        ) : undefined
      }
      right={
        showNavigation && options.length > 0 ? (
          <NavigableControlArrow
            direction="next"
            size={props.size}
            onClick={() => nextIndex !== currentIndex && props.onChange(navigableOptions.at(nextIndex))}
            className="subaccount-next"
            tooltip="Next Favorite Sub Account"
          />
        ) : undefined
      }
    >
      {mainField}
    </NavigableControlRow>
  );
}
